import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart3, FileText, Users, Download, LogOut, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AdminSubNav from "../components/AdminSubNav";
import InboxBell from "../components/InboxBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, setUser }) => {
  const [stats, setStats] = useState(null);
  const [filesPerDay, setFilesPerDay] = useState([]);
  const [adminUploads, setAdminUploads] = useState([]);
  const [daysWindow, setDaysWindow] = useState(30);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null);

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(`${API}/admin/digest/send?force=true`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Digest sent to ${resp.data.recipients} admin(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the digest");
    } finally {
      setSendingDigest(false);
    }
  };
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    const token = localStorage.getItem("access_token");
    axios
      .get(`${API}/admin/email-health`, { headers: { Authorization: `Bearer ${token}` } })
      .then((resp) => setEmailHealth(resp.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchFilesPerDay(daysWindow);
    fetchAdminUploads(daysWindow);
  }, [daysWindow]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilesPerDay = async (days) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/files-per-day?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilesPerDay(response.data.data || []);
    } catch (error) {
      console.error('Files-per-day error:', error);
    }
  };

  const fetchAdminUploads = async (days) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/items-by-uploader?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminUploads(response.data.admins || []);
    } catch (error) {
      console.error('Admin-uploads error:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-white hover:text-white hover:bg-slate-800" data-testid="admin-home-btn">
                Public Site
              </Button>
              <div className="text-white">
                <InboxBell />
              </div>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white hover:text-white hover:bg-slate-800" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="overview" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {emailHealth && !emailHealth.healthy && (
          <div className="mb-6 rounded-lg border border-red-300 bg-red-50 px-5 py-4" data-testid="email-health-alert">
            <p className="text-sm font-semibold text-red-900">
              ⚠️ Email service is not configured — password reset, welcome and digest emails are NOT being sent.
            </p>
            <p className="text-xs text-red-800 mt-1">
              Missing: {emailHealth.missing.join(", ")}. Set {emailHealth.missing.length > 1 ? "them" : "it"} in the
              Deployment Panel → Secrets tab (production) or backend/.env (preview), then redeploy/restart.
            </p>
          </div>
        )}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <>
            {/* Stats */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Overview</h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={sendingDigest}
                  onClick={sendDigestNow}
                  data-testid="send-digest-now-btn"
                >
                  <Mail className="w-3 h-3 mr-1" />
                  {sendingDigest ? "Sending…" : "Send digest now"}
                </Button>
                <button
                  type="button"
                  className="flex items-center text-xs text-slate-200 hover:text-white border border-slate-500 rounded-full px-2 py-1"
                  onClick={() => alert('Overview shows total items, users, active users (trial + subscribed), and downloads. Use the Items, Users, Logs, and Submissions tabs above for detailed management.')}
                >
                  <span className="inline-block w-4 h-4 mr-1 rounded-full border border-slate-300 flex items-center justify-center text-[10px]">?</span>
                  Help
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white border border-slate-200 p-6 rounded-md" data-testid="stat-items">
                <FileText className="w-8 h-8 text-orange-500 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
                  {stats?.total_items || 0}
                </div>
                <div className="text-sm text-slate-600">Total Items</div>
              </div>
              <div className="bg-white border border-slate-200 p-6 rounded-md" data-testid="stat-users">
                <Users className="w-8 h-8 text-orange-500 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
                  {stats?.total_users || 0}
                </div>
                <div className="text-sm text-slate-600">Total Users</div>
              </div>
              <div className="bg-white border border-slate-200 p-6 rounded-md" data-testid="stat-active-users">
                <Users className="w-8 h-8 text-green-500 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
                  {stats?.total_active_users || 0}
                </div>
                <div className="text-sm text-slate-600">Active Users (trial + subscribed)</div>
              </div>
              <div className="bg-white border border-slate-200 p-6 rounded-md" data-testid="stat-downloads">
                <Download className="w-8 h-8 text-orange-500 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
                  {stats?.total_downloads || 0}
                </div>
                <div className="text-sm text-slate-600">Total Downloads</div>
              </div>
              <div className="bg-white border border-slate-200 p-6 rounded-md" data-testid="stat-activity">
                <BarChart3 className="w-8 h-8 text-orange-500 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>Active</div>
                <div className="text-sm text-slate-600">System Status</div>
              </div>
            </div>

            {/* Export CSV for files per day */}
            <div className="bg-white border border-slate-200 p-4 rounded-md mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Export File CSV</h2>
                <p className="text-sm text-slate-600">Download CSV summary for files loaded per day.</p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    const rows = ["date,admin_files,client_files,total_files"]; 
                    filesPerDay.forEach(row => {
                      rows.push(`${row.date},${row.admin_count},${row.client_count},${row.total}`);
                    });
                    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", `files-per-day-${daysWindow}d.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  }}
                >
                  Export File CSV
                </Button>
              </div>
            </div>

            {/* Files per Day */}
            <div className="bg-white border border-slate-200 p-6 rounded-md mb-6">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Fraunces, serif' }}>Files Loaded per Day</h2>
              {filesPerDay && filesPerDay.length > 0 ? (
                <div className="max-h-64 overflow-y-auto border border-slate-100 rounded">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-slate-600">Date</th>
                        <th className="px-4 py-2 text-right text-slate-600">Admin Files</th>
                        <th className="px-4 py-2 text-right text-slate-600">Client Files</th>
                        <th className="px-4 py-2 text-right text-slate-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filesPerDay.map((row) => (
                        <tr key={row.date} className="border-t border-slate-100">
                          <td className="px-4 py-1.5 text-slate-700">{row.date}</td>
                          <td className="px-4 py-1.5 text-right font-mono text-slate-800">{row.admin_count}</td>
                          <td className="px-4 py-1.5 text-right font-mono text-slate-800">{row.client_count}</td>
                          <td className="px-4 py-1.5 text-right font-mono text-slate-800">{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-600 text-sm">No files recorded in the selected period.</p>
              )}
            </div>

            {/* Admin uploads by uploader */}
            <div className="bg-white border border-slate-200 p-6 rounded-md mb-6">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
                Files Loaded by Admin (last {daysWindow} days)
              </h2>
              {adminUploads && adminUploads.length > 0 ? (
                <div className="max-h-64 overflow-y-auto border border-slate-100 rounded">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-slate-600">Admin</th>
                        <th className="px-4 py-2 text-left text-slate-600">Organization</th>
                        <th className="px-4 py-2 text-right text-slate-600">Files</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminUploads.map((row) => (
                        <tr key={row.user_id} className="border-t border-slate-100">
                          <td className="px-4 py-1.5 text-slate-700">
                            {row.name || row.email || row.user_id}
                          </td>
                          <td className="px-4 py-1.5 text-slate-700">{row.organization || 'Public'}</td>
                          <td className="px-4 py-1.5 text-right font-mono text-slate-800">{row.item_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-600 text-sm">No admin-uploaded files recorded in the selected period.</p>
              )}
            </div>

            {/* Recent Items */}
            <div className="bg-white border border-slate-200 p-6 rounded-md">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Fraunces, serif' }}>Recent Items</h2>
              {stats?.recent_items && stats.recent_items.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 border border-slate-200 rounded cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/items/${item.id}`)}
                      data-testid={`recent-item-${item.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {item.has_file && item.file_type === 'PDF' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/items/${item.id}`);
                            }}
                            className="flex-shrink-0 border border-slate-200 rounded-md overflow-hidden bg-slate-100 hover:bg-slate-200 transition"
                            aria-label={`Open ${item.title}`}
                          >
                            <img
                              src={`${API}/items/${item.id}/thumbnail`}
                              alt={`First page of ${item.title}`}
                              className="h-16 w-12 object-cover"
                            />
                          </button>
                        )}
                        <div>
                          <div className="font-medium text-slate-900">{item.title}</div>
                          <div className="text-sm text-slate-600">{item.authors?.join(', ') || 'No authors'}</div>
                        </div>
                      </div>
                      <div className="text-xs text-slate-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {new Date(item.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-600">No items yet</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;